<template>
  <div>
    <nav class=" bg-mm-colors-sidebarBackground place-self-stretch border-r border-gray-200" :style="setZIndex(true)">
      <div class="relative h-screen flex flex-col flex-1 py-4 w-20">
        <div class="flex items-center h-10">
          <img src="../../assets/images/logo.png" alt="Momentum Logo" class="p-4">
        </div>
        <div class="flex flex-col items-center h-full py-2 text-gray-600 mt-5">
          <!-- Calls -->
          <div class="2xl:w-full">
            <router-link :to="{ name: 'user-call-library' }" class="group relative flex w-full 2xl:mt-1" :class="{ 'active-route': isActiveRoute(['user-call-library', 'user-playlist-details']) }">
              <div class="text-center w-full p-2 group-hover:text-mm-colors-textPrimary active-route-link">
                <div class="p-2 inline-block bg-gray-100 rounded-md group-hover:bg-mm-colors-sidebarHighlight transition-all duration-300 ease-in-out icon-wrapper">
                  <CIcon icon="IconVideo" class="w-6 h-6 m-auto active-route-link-icon" />
                </div>
                <p class="text-xs active-route-link-title">
                  Calls
                </p>
              </div>
            </router-link>
          </div>
          <!-- Ask Momentum -->
          <div class="2xl:w-full">
            <router-link v-show="ldFlags.flags['user.app.ask.momentum'] && !isExactRoleViewer()" :to="{ name: 'ask-momentum-page' }" class="group relative flex w-full 2xl:mt-1" :class="{ 'active-route': isActiveRoute(['ask-momentum-page', 'ask-momentum-conversation']) }">
              <CBadge :show-badge="false" class="w-full">
                <p class="absolute -top-1 -right-1 bg-green-200 rounded-full px-1 font-medium text-xs z-10">
                  Beta
                </p>
                <div class="text-center w-full p-2 group-hover:text-mm-colors-textPrimary active-route-link">
                  <div class="p-2 inline-block bg-gray-100 rounded-md group-hover:bg-mm-colors-sidebarHighlight transition-all duration-300 ease-in-out icon-wrapper">
                    <CIcon icon="IconChatBubbleLeft" class="w-6 h-6 m-auto active-route-link-icon" />
                  </div>
                  <p class="text-xs active-route-link-title">
                    Ask
                  </p>
                </div>
              </CBadge>
            </router-link>
          </div>
          <!-- Accounts -->
          <div class="2xl:w-full">
            <router-link v-show="accountsAccess.all" :to="{ name: 'account-briefs' }" class="group relative flex w-full 2xl:mt-1" :class="{ 'active-route': isActiveRoute(['account-briefs', 'account-brief-view']) }">
              <div class="text-center w-full p-2 group-hover:text-mm-colors-textPrimary active-route-link">
                <div class="p-2 inline-block bg-gray-100 rounded-md group-hover:bg-mm-colors-sidebarHighlight transition-all duration-300 ease-in-out icon-wrapper">
                  <CIcon icon="IconCalendarDays" class="w-6 h-6 m-auto active-route-link-icon" />
                </div>
                <p class="text-xs active-route-link-title">
                  Accounts
                </p>
              </div>
            </router-link>
          </div>
          <!-- Autopilot -->
          <div class="2xl:w-full">
            <router-link :to="{ name: 'autopilot' }" class="group relative flex w-full 2xl:mt-1" :class="{ 'active-route': isActiveRoute(['autopilot']) }">
              <div class="text-center w-full p-2 group-hover:text-mm-colors-textPrimary active-route-link">
                <div class="p-2 inline-block bg-gray-100 rounded-md group-hover:bg-mm-colors-sidebarHighlight transition-all duration-300 ease-in-out icon-wrapper">
                  <CIcon icon="IconAutoPilot" class="w-6 h-6 m-auto active-route-link-icon" />
                </div>
                <p class="text-xs active-route-link-title">
                  Autopilot
                </p>
              </div>
            </router-link>
          </div>
          <!-- Email Follow-Up -->
          <div class="2xl:w-full">
            <router-link :to="{ name: 'email-follow-up' }" class="group relative flex w-full 2xl:mt-1" :class="{ 'active-route': isActiveRoute(['email-follow-up', 'user-email-follow-up-config']) }">
              <div class="text-center w-full p-2 group-hover:text-mm-colors-textPrimary active-route-link">
                <div class="p-2 inline-block bg-gray-100 rounded-md group-hover:bg-mm-colors-sidebarHighlight transition-all duration-300 ease-in-out icon-wrapper">
                  <CIcon icon="IconEmail" class="w-6 h-6 m-auto active-route-link-icon" />
                </div>
                <p class="text-xs active-route-link-title">
                  Email Follow-Up
                </p>
              </div>
            </router-link>
          </div>
        </div>
        <div class="w-full flex items-center justify-center px-2 border-t border-gray-200">
          <NDropdown trigger="click" placement="top-start" :options="profileOptions" @select="handleOptionClick">
            <div class="flex items-center justify-center">
              <button type="button" class="rounded-full mt-3 focus:outline-none focus:ring focus:ring-blue-500">
                <MProfileImage :name="profile?.name" :image-src="profile?.picture" alt-text="User's Avatar" :custom-class="`!w-10 !h-10 cursor-pointer rounded-full text-xl border`" />
              </button>
            </div>
          </NDropdown>
        </div>
      </div>
      <PopUpNotifications />
    </nav>
  </div>
</template>

<script lang="ts">
import { NDropdown } from "naive-ui";
import { DropdownMixedOption } from "naive-ui/lib/dropdown/src/interface";
import { defineComponent, PropType } from "vue";
import { RouteLocationNormalizedLoaded, useRoute } from "vue-router";

import { setZIndex } from "../../components/library/utilities";
import PopUpNotifications from "../../components/notifications/PopUpNotifications.vue";
import { useLaunchDarklyStore } from "../../store/launch-darkly";
import { useUserRoleAccessStore } from "../../store/user-role-access";
import { IAuthProfile } from "../../types/momentum";
import { isExactRoleViewer } from "../../utils/roles";
import MProfileImage from "../reusable-components/MProfileImage.vue";

export default defineComponent({
  components: {
    MProfileImage,
    NDropdown,
    PopUpNotifications,
  },
  emits: ["update:profile-option-clicked"],
  props: {
    profile: {
      default: null,
      type: Object as PropType<IAuthProfile>,
    },
    profileOptions: {
      default: () => [],
      type: Array as PropType<DropdownMixedOption[]>,
    },
  },
  setup(props, { emit }) {
    const { accountsAccess } = useUserRoleAccessStore();
    const { ldFlags } = useLaunchDarklyStore();
    const route: RouteLocationNormalizedLoaded = useRoute();

    function handleOptionClick(value: string): void {
      emit("update:profile-option-clicked", value);
    }

    function isActiveRoute(routeNames: string[]): boolean {
      return routeNames?.includes(route.name as string);
    }

    return {
      accountsAccess,
      handleOptionClick,
      isActiveRoute,
      isExactRoleViewer,
      ldFlags,
      setZIndex,
    };
  },
});
</script>
